import { Button, Modal, Typography, Upload } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import { UploadOutlined } from "@ant-design/icons";
import { useState } from "react";
import { apiClient } from "../../utils/httpClient";
import { getErrorMessage, Notify } from "../../utils/constants";
import { ModalProps } from "../../utils/types";

const ALLOWED_EXTENSIONS = ["pdf", "jpg", "jpeg"];

type AttachDocumentModalProps = ModalProps & {
  requestId: number | null;
  documentType: string;
  onUploaded?: () => void;
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const AttachDocumentModal = ({
  isOpen,
  setIsOpen,
  requestId,
  documentType,
  onUploaded,
}: AttachDocumentModalProps) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUpload = async () => {
    const file = fileList[0]?.originFileObj as File | undefined;
    if (!file || !requestId) {
      Notify("error", "Please select a file to upload.");
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      Notify("error", "Only PDF and JPG/JPEG files are accepted.");
      return;
    }

    setIsSubmitting(true);
    try {
      const fileBase64 = await fileToBase64(file);
      await apiClient.post(
        `${process.env.REACT_APP_API_URL}/leave-requests/${requestId}/documents`,
        {
          documentType,
          fileBase64,
          fileName: file.name,
          mimeType: file.type,
        },
      );
      Notify("success", "Document uploaded successfully.");
      setFileList([]);
      setIsOpen(false);
      if (onUploaded) onUploaded();
    } catch (error) {
      console.error("Failed to upload document:", error);
      Notify(
        "error",
        getErrorMessage(error, "Could not upload the document."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title="Upload document"
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      footer={[
        <Button key="cancel" onClick={() => setIsOpen(false)}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<UploadOutlined />}
          loading={isSubmitting}
          disabled={fileList.length === 0}
          onClick={handleUpload}
        >
          Upload
        </Button>,
      ]}
    >
      <Typography.Paragraph type="secondary">
        Accepted formats: PDF, JPG, JPEG.
      </Typography.Paragraph>
      <Upload
        beforeUpload={() => false}
        fileList={fileList}
        onChange={({ fileList: newFileList }) =>
          setFileList(newFileList.slice(-1))
        }
        maxCount={1}
      >
        <Button icon={<UploadOutlined />}>Select file</Button>
      </Upload>
    </Modal>
  );
};

export default AttachDocumentModal;
